import { type ISuggestion, type Operator, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RETRIEVE_BY_QUERY, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import {
    type IFieldCustomValue,
    type IPrivateFieldCustomValue,
    privateFieldCustomListValueProperties,
    privateFieldCustomValuesManager,
    sharedFieldCustomListValueProperties,
    sharedFieldCustomValuesManager
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { LIMIT_TOTAL_DISPLAY, MAX_RESULTS } from '@/constants/field-custom-value.constants';
import { stringService } from '@/services/string-service';
import { SHARED_FIELD_CUSTOM_VALUE_MODULE } from '@/store/shared-field-custom-value-module';

export const RETRIEVE_TYPEAHEAD = 'RETRIEVE_TYPEAHEAD';
export const MERGE = 'MERGE';
export const REORDER = 'REORDER';

function sortResults(listArray: Array<ISuggestion<IFieldCustomValue>>): Array<ISuggestion<IFieldCustomValue>> {
    return listArray.sort((suggestion1: ISuggestion<IFieldCustomValue>, suggestion2: ISuggestion<IFieldCustomValue>) => {
        return suggestion2.score - suggestion1.score;
    });
}

function sortResultsAZ(listArray: Array<ISuggestion<IFieldCustomValue>>): Array<ISuggestion<IFieldCustomValue>> {
    return listArray.sort((suggestion1: ISuggestion<IFieldCustomValue>, suggestion2: ISuggestion<IFieldCustomValue>) => {
        return suggestion1.dataObject.label.localeCompare(suggestion2.dataObject.label);
    });
}

function sortResultsPosition(listArray: Array<ISuggestion<IFieldCustomValue>>): Array<ISuggestion<IFieldCustomValue>> {
    return listArray.sort((suggestion1: ISuggestion<IFieldCustomValue>, suggestion2: ISuggestion<IFieldCustomValue>) => {
        return suggestion1.dataObject.position - suggestion2.dataObject.position;
    });
}

export const actions: ActionTree<IPrivateFieldCustomValue, unknown> = {
    async [REORDER](context, { ids, listId, properties }: { ids: number[]; listId: string; properties?: string }): Promise<number[]> {
        const dataObjectList = await privateFieldCustomValuesManager.reorder(ids, listId, properties);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList });
        return dataObjectList.map((value) => {
            return value.dobjId;
        });
    },
    async [MERGE](
        context,
        { dobjIds, target, properties }: { dobjIds: number[]; target?: IPrivateFieldCustomValue; properties?: string }
    ): Promise<IFieldCustomValue> {
        if (target.dobjId === undefined) {
            target.dobjId = dobjIds.shift();
        }
        await privateFieldCustomValuesManager.deduplicate(dobjIds, target, properties);
        return target;
    },
    async [RETRIEVE_TYPEAHEAD](
        context,
        {
            searchTerm = '',
            listId,
            directoryListId,
            hasPosition,
            filter,
            directoryFilter
        }: { searchTerm: string; listId: string; directoryListId?: string; hasPosition?: boolean; filter?: Operator; directoryFilter?: Operator }
    ): Promise<{ exactMatch: boolean; response: IFieldCustomValue[]; totalCount: number }> {
        let list: IFieldCustomValue[];
        let exactMatch = false;
        let totalCount: number;
        searchTerm = stringService.cleanText(searchTerm);
        const comparator = queryBuilder.cmp.equalTo('listId', listId);
        if (searchTerm?.length > 1) {
            if (directoryListId?.length > 0) {
                const directoryResponse = await sharedFieldCustomValuesManager.typeahead(
                    searchTerm,
                    MAX_RESULTS,
                    queryBuilder.op.and(queryBuilder.op.and(queryBuilder.cmp.equalTo('listId', directoryListId)), directoryFilter),
                    sharedFieldCustomListValueProperties.DEFAULT
                );
                const linkPromise = context.dispatch(RETRIEVE_BY_QUERY, {
                    query: queryBuilder.build(
                        queryBuilder.op.and(
                            queryBuilder.op.and(
                                queryBuilder.cmp.isIn(
                                    'copyOf',
                                    directoryResponse.map((directorySuggestion) => {
                                        return directorySuggestion.dataObject.dobjId;
                                    })
                                ),
                                comparator
                            ),
                            filter
                        )
                    ),
                    properties: privateFieldCustomListValueProperties.DEFAULT
                });
                const queryPromise = privateFieldCustomValuesManager.typeahead(
                    searchTerm,
                    MAX_RESULTS,
                    queryBuilder.op.and(queryBuilder.op.and(comparator), filter),
                    privateFieldCustomListValueProperties.DEFAULT
                );
                return Promise.all([linkPromise, queryPromise]).then((data: any[][]) => {
                    return {
                        response: sortResults(
                            data[1].concat(
                                directoryResponse.filter((directorySuggestion) => {
                                    const response =
                                        data[1].filter((privateSuggestion) => {
                                            return privateSuggestion.dataObject.copyOf?.dobjId === directorySuggestion.dataObject.dobjId;
                                        }).length === 0;
                                    if (response) {
                                        data[0].forEach((linkId) => {
                                            const linkObject = context.getters[GET_BY_ID](linkId);
                                            if (linkObject.copyOf?.dobjId === directorySuggestion.dataObject.dobjId) {
                                                directorySuggestion.dataObject = linkObject;
                                            }
                                        });
                                    }
                                    return response;
                                })
                            )
                        ).map((element) => {
                            if (stringService.cleanText(element.dataObject.label) === searchTerm) {
                                exactMatch = true;
                            }
                            return element.dataObject;
                        }),
                        exactMatch,
                        totalCount: null
                    };
                });
            }
            list = await privateFieldCustomValuesManager
                .typeahead(searchTerm, MAX_RESULTS, queryBuilder.op.and(queryBuilder.op.and(comparator), filter), privateFieldCustomListValueProperties.DEFAULT)
                .then((results) => {
                    return results.map((result) => {
                        if (stringService.cleanText(result.dataObject.label) === searchTerm) {
                            exactMatch = true;
                        }
                        return result.dataObject;
                    });
                });
        } else {
            let directoryCount = 0;
            let privateCount = 0;
            if (directoryListId?.length > 0) {
                directoryCount = await sharedFieldCustomValuesManager.count(
                    queryBuilder.build(
                        queryBuilder.op.and(queryBuilder.op.and(queryBuilder.cmp.equalTo('listId', directoryListId)), directoryFilter),
                        0,
                        null,
                        [
                            {
                                attribute: 'label',
                                direction: 'ASC'
                            }
                        ]
                    )
                );
                privateCount = await privateFieldCustomValuesManager.count(
                    queryBuilder.build(queryBuilder.op.and(queryBuilder.op.and(comparator, queryBuilder.cmp.isNull('copyOf')), filter), 0, null, [
                        {
                            attribute: 'label',
                            direction: 'ASC'
                        }
                    ])
                );
            } else {
                privateCount = await privateFieldCustomValuesManager.count(
                    queryBuilder.build(queryBuilder.op.and(queryBuilder.op.and(comparator), filter), 0, null, [
                        {
                            attribute: 'label',
                            direction: 'ASC'
                        }
                    ])
                );
            }
            totalCount = privateCount + directoryCount;
            let maxResults: number;
            if (totalCount > LIMIT_TOTAL_DISPLAY) {
                maxResults = MAX_RESULTS;
            } else {
                maxResults = LIMIT_TOTAL_DISPLAY;
            }
            if (directoryListId?.length > 0) {
                const directoryResponse = (
                    await context.dispatch(
                        `${SHARED_FIELD_CUSTOM_VALUE_MODULE}/${RETRIEVE_BY_QUERY}`,
                        {
                            query: queryBuilder.build(
                                queryBuilder.op.and(
                                    queryBuilder.op.and(
                                        queryBuilder.cmp.equalTo('listId', directoryListId),
                                        queryBuilder.cmp.startWithIgnoreCase('label', searchTerm)
                                    ),
                                    directoryFilter
                                ),
                                0,
                                maxResults,
                                [
                                    {
                                        attribute: 'label',
                                        direction: 'ASC'
                                    }
                                ]
                            ),
                            properties: sharedFieldCustomListValueProperties.DEFAULT
                        },
                        { root: true }
                    )
                ).map((directoryFieldCustomValue: number) => {
                    return { dataObject: context.rootGetters[`${SHARED_FIELD_CUSTOM_VALUE_MODULE}/${GET_BY_ID}`](directoryFieldCustomValue) };
                });
                const linkPromise = context.dispatch(RETRIEVE_BY_QUERY, {
                    query: queryBuilder.build(
                        queryBuilder.op.and(
                            queryBuilder.op.and(
                                queryBuilder.cmp.isIn(
                                    'copyOf',
                                    directoryResponse.map((directorySuggestion: ISuggestion<IFieldCustomValue>) => {
                                        return directorySuggestion.dataObject.dobjId;
                                    })
                                ),
                                comparator
                            ),
                            filter
                        )
                    ),
                    properties: privateFieldCustomListValueProperties.DEFAULT
                });
                const queryPromise = context.dispatch(RETRIEVE_BY_QUERY, {
                    query: queryBuilder.build(
                        queryBuilder.op.and(queryBuilder.op.and(comparator, queryBuilder.cmp.startWithIgnoreCase('label', searchTerm)), filter),
                        0,
                        maxResults,
                        [
                            {
                                attribute: 'label',
                                direction: 'ASC'
                            }
                        ]
                    ),
                    properties: privateFieldCustomListValueProperties.DEFAULT
                });
                return Promise.all([linkPromise, queryPromise]).then((data: any[][]) => {
                    data[1] = data[1].map((privateFieldCustomValue) => {
                        return { dataObject: context.getters[GET_BY_ID](privateFieldCustomValue) };
                    });
                    return {
                        response: (hasPosition ? sortResultsPosition : sortResultsAZ)(
                            data[1].concat(
                                directoryResponse.filter((directorySuggestion: ISuggestion<IFieldCustomValue>) => {
                                    const response =
                                        data[1].filter((privateSuggestion) => {
                                            return privateSuggestion.dataObject.copyOf?.dobjId === directorySuggestion.dataObject.dobjId;
                                        }).length === 0;
                                    if (response) {
                                        data[0].forEach((linkId) => {
                                            const linkObject = context.getters[GET_BY_ID](linkId);
                                            if (linkObject.copyOf?.dobjId === directorySuggestion.dataObject.dobjId) {
                                                directorySuggestion.dataObject = linkObject;
                                            }
                                        });
                                    }
                                    return response;
                                })
                            )
                        ).map((element) => {
                            if (stringService.cleanText(element.dataObject.label) === searchTerm) {
                                exactMatch = true;
                            }
                            return element.dataObject;
                        }),
                        exactMatch,
                        totalCount
                    };
                });
            }
            list = (
                await context.dispatch(RETRIEVE_BY_QUERY, {
                    query: queryBuilder.build(
                        queryBuilder.op.and(queryBuilder.op.and(comparator, queryBuilder.cmp.startWithIgnoreCase('label', searchTerm)), filter),
                        0,
                        maxResults,
                        [
                            hasPosition
                                ? {
                                      attribute: 'position',
                                      direction: 'ASC'
                                  }
                                : {
                                      attribute: 'label',
                                      direction: 'ASC'
                                  }
                        ]
                    ),
                    properties: privateFieldCustomListValueProperties.DEFAULT
                })
            ).map((dobjId: number) => {
                return context.getters[GET_BY_ID](dobjId);
            });
        }
        return { response: list, exactMatch, totalCount };
    }
};
